export type ScheduleMobileWindow<TUpcomingGame, TCompletedGame> = {
  completed: TCompletedGame[];
  hasMoreCompleted: boolean;
  hasMoreUpcoming: boolean;
  nextGame?: TUpcomingGame;
  recentGame?: TCompletedGame;
  upcoming: TUpcomingGame[];
};

export const selectScheduleMobileWindow = <TUpcomingGame, TCompletedGame>({
  completed,
  completedLimit = 5,
  upcoming,
  upcomingLimit = 10,
}: {
  completed: TCompletedGame[];
  completedLimit?: number;
  upcoming: TUpcomingGame[];
  upcomingLimit?: number;
}): ScheduleMobileWindow<TUpcomingGame, TCompletedGame> => ({
  completed: completed.slice(0, completedLimit),
  hasMoreCompleted: completed.length > completedLimit,
  hasMoreUpcoming: upcoming.length > upcomingLimit,
  nextGame: upcoming[0],
  recentGame: completed[0],
  upcoming: upcoming.slice(0, upcomingLimit),
});

type GameTemplateTeam = {
  players?: Record<string, unknown>[];
  pts: number;
  tid: number;
  [key: string]: unknown;
};

type GameTemplateBoxScore = {
  gid: number;
  teams?: [GameTemplateTeam, GameTemplateTeam];
};

const getTeamIndex = (game: GameTemplateBoxScore, tid: number) => {
  const index = game.teams?.findIndex((team) => team.tid === tid) ?? -1;
  return index === -1 ? 0 : (index as 0 | 1);
};

const getOpponentIndex = (index: 0 | 1) => (index === 0 ? 1 : 0);

const numberStat = (team: Record<string, unknown>, stat: string) => {
  const value = team[stat];
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
};

export type GameLogConclusion = {
  keyDifferences: {
    key: "freeThrows" | "points" | "rebounds" | "turnovers";
    value: number;
  }[];
  opponentTid: number;
  result: "loss" | "tie" | "win";
  score: [number, number];
  teamIndex: 0 | 1;
  topPlayer?: {
    name?: string;
    pts: number;
  };
};

export const getGameLogConclusion = ({
  boxScore,
  tid,
}: {
  boxScore: GameTemplateBoxScore;
  tid: number;
}): GameLogConclusion | undefined => {
  if (boxScore.gid < 0 || !boxScore.teams?.[0] || !boxScore.teams[1]) {
    return;
  }

  const teamIndex = getTeamIndex(boxScore, tid);
  const opponentIndex = getOpponentIndex(teamIndex);
  const team = boxScore.teams[teamIndex]!;
  const opponent = boxScore.teams[opponentIndex]!;
  const teamScore = numberStat(team, "pts");
  const opponentScore = numberStat(opponent, "pts");
  const topPlayer = [...(team.players ?? [])]
    .sort((a, b) => numberStat(b, "pts") - numberStat(a, "pts"))
    .at(0);

  return {
    keyDifferences: [
      { key: "points", value: teamScore - opponentScore },
      { key: "rebounds", value: numberStat(team, "trb") - numberStat(opponent, "trb") },
      { key: "turnovers", value: numberStat(team, "tov") - numberStat(opponent, "tov") },
      { key: "freeThrows", value: numberStat(team, "ft") - numberStat(opponent, "ft") },
    ],
    opponentTid: opponent.tid,
    result: teamScore > opponentScore ? "win" : teamScore < opponentScore ? "loss" : "tie",
    score: [teamScore, opponentScore],
    teamIndex,
    topPlayer:
      topPlayer && numberStat(topPlayer, "pts") > 0
        ? {
            name: typeof topPlayer.name === "string" ? topPlayer.name : undefined,
            pts: numberStat(topPlayer, "pts"),
          }
        : undefined,
  };
};

type PlayoffSeriesTeam = {
  tid: number;
  won?: number;
};

type PlayoffSeries = {
  away?: PlayoffSeriesTeam;
  gids?: number[];
  home?: PlayoffSeriesTeam;
};

export const isHistoricalPlayoffsSeason = ({
  currentSeason,
  viewSeason,
}: {
  currentSeason: number;
  viewSeason: number;
}) => viewSeason < currentSeason;

export type UserPlayoffSeries<TSeries> = {
  roundIndex: number;
  series: TSeries;
  seriesIndex: number;
};

export const findUserPlayoffSeries = <TSeries extends PlayoffSeries>({
  series,
  userTid,
}: {
  series: TSeries[][];
  userTid: number;
}): UserPlayoffSeries<TSeries> | undefined => {
  for (let roundIndex = series.length - 1; roundIndex >= 0; roundIndex -= 1) {
    const round = series[roundIndex]!;
    for (const [seriesIndex, seriesInfo] of round.entries()) {
      if (seriesInfo.home?.tid === userTid || seriesInfo.away?.tid === userTid) {
        return {
          roundIndex,
          series: seriesInfo,
          seriesIndex,
        };
      }
    }
  }
};

export type PlayoffPathStatus =
  | "advanced"
  | "eliminated"
  | "notQualified"
  | "leading"
  | "projected"
  | "tied"
  | "trailing";

export const getPlayoffPathContext = <TSeries extends PlayoffSeries>({
  finalMatchups,
  numGamesToWinSeries,
  numRounds,
  userSeries,
  userTid,
}: {
  finalMatchups: boolean;
  numGamesToWinSeries: number[];
  numRounds: number;
  userSeries?: UserPlayoffSeries<TSeries>;
  userTid: number;
}) => {
  if (!userSeries) {
    return {
      hasUserSeries: false,
      latestGameID: undefined,
      opponentWins: 0,
      pathRoundsRemaining: numRounds,
      roundIndex: undefined,
      status: finalMatchups ? "notQualified" : "projected",
      userWins: 0,
      winsNeededThisSeries: undefined,
    } as const;
  }

  const { roundIndex, series } = userSeries;
  const userTeam = series.home?.tid === userTid ? series.home : series.away;
  const opponentTeam = series.home?.tid === userTid ? series.away : series.home;
  const userWins = userTeam?.won ?? 0;
  const opponentWins = opponentTeam?.won ?? 0;
  const gamesToWin = numGamesToWinSeries[roundIndex] ?? 1;
  const status: PlayoffPathStatus =
    userWins >= gamesToWin
      ? "advanced"
      : opponentWins >= gamesToWin
        ? "eliminated"
        : !finalMatchups
          ? "projected"
          : userWins > opponentWins
            ? "leading"
            : userWins < opponentWins
              ? "trailing"
              : "tied";

  return {
    hasUserSeries: true,
    latestGameID: series.gids?.at(-1),
    opponentWins,
    pathRoundsRemaining: Math.max(0, numRounds - roundIndex),
    roundIndex,
    status,
    userWins,
    winsNeededThisSeries: Math.max(0, gamesToWin - userWins),
  };
};

// N3-B: the top-of-page "your playoff path" summary bar collapses the seven
// fine-grained path states into the three states the bar renders — still in the
// player's own team (active), knocked out (eliminated), or never got in
// (notQualified). Pure so the three-state coverage is unit-testable.
export type PlayoffSummaryTone = "active" | "eliminated" | "notQualified";

export const getPlayoffSummaryTone = (
  status: PlayoffPathStatus,
): PlayoffSummaryTone => {
  if (status === "eliminated") {
    return "eliminated";
  }
  if (status === "notQualified") {
    return "notQualified";
  }
  return "active";
};

// N3-B: per-round status pill. A round with no matchups yet is "notStarted"
// (renders a TBD placeholder so a not-yet-drawn round is never a blank gap);
// a round whose every series has a winner (including a bye) is "completed";
// anything in between is "inProgress".
export type PlayoffRoundStatus = "completed" | "inProgress" | "notStarted";

export const getPlayoffRoundStatus = <TSeries extends PlayoffSeries>({
  round,
  winsNeeded,
}: {
  round: TSeries[] | undefined;
  winsNeeded: number | undefined;
}): PlayoffRoundStatus => {
  if (!round || round.length === 0) {
    return "notStarted";
  }

  const need = winsNeeded ?? 1;
  const seriesDecided = (seriesInfo: TSeries) => {
    if (!seriesInfo.away) {
      // A bye advances the home team without games.
      return true;
    }
    return (
      (seriesInfo.home?.won ?? 0) >= need || (seriesInfo.away.won ?? 0) >= need
    );
  };

  return round.every(seriesDecided) ? "completed" : "inProgress";
};

export const getLiveGameShellContract = ({
  gameOver,
  mode,
}: {
  gameOver?: boolean;
  mode: "full" | "highlight";
}) => ({
  controlPlacement: "bottom",
  exitTarget: "games",
  fullscreen: true,
  mode,
  playState: gameOver ? "final" : "live",
});

export type LiveGameSchedulePath =
  | ["daily_schedule"]
  | ["daily_schedule", number, number];

export const getLiveGameSchedulePath = ({
  day,
  season,
}: {
  day?: unknown;
  season?: unknown;
}): LiveGameSchedulePath =>
  typeof day === "number" &&
  Number.isInteger(day) &&
  typeof season === "number" &&
  Number.isInteger(season)
    ? ["daily_schedule", season, day]
    : ["daily_schedule"];

export const getLiveGamePrimaryState = ({ gameOver }: { gameOver?: boolean }) => ({
  nextAction: {
    defaultLabel: gameOver ? "Next matchday" : "Keep watching",
    labelKey: gameOver ? "liveGame.nextMatchday" : "liveGame.keepWatching",
    path: ["daily_schedule"],
  },
  status: gameOver ? "final" : "live",
  statusKey: gameOver ? "liveGame.finalStatus" : "liveGame.liveStatus",
  defaultStatus: gameOver ? "Final" : "Live",
});
